import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import qs from 'qs';
import _get from 'lodash/get';
import useSiteCopySelector from 'components/useSiteCopySelector';
import useSiteConfigSelector from 'components/useSiteConfigSelector';
import useIsAuthPath from 'components/Hooks/useIsAuthPath';
import { makeSelectAuth } from 'containers/Auth/selectors';
import { makeSelectClientDetails } from 'containers/Main/selectors';
import { hasClientAccessToFeature } from 'utils/clientSettings';

export default function useEmailLoginReminder() {
  const location = useLocation();
  const history = useHistory();
  const auth = useSelector(makeSelectAuth());
  const clientDetails = useSelector(makeSelectClientDetails());
  const allowSignIn = clientDetails?.signIn === 'Allow';
  const [siteCopy] = useSiteCopySelector(['sign-in']);
  const [siteConfig] = useSiteConfigSelector(['Features']);
  const [open, setOpen] = useState();
  const [reminderWasShown, setReminderWasShown] = useState();
  const isAuthPage = useIsAuthPath();

  const isCTAModalEnabled =
    allowSignIn &&
    hasClientAccessToFeature({
      featuresSiteConfig: siteConfig,
      featureName: 'authenticationCallToActionModal',
      clientDetails,
    });

  const query = useMemo(() => {
    const search = qs.parse(location.search, { ignoreQueryPrefix: true });
    const hashQuery = location.hash.split('?')[1] || '';
    const hash = qs.parse(hashQuery);
    return { ...search, ...hash };
  }, [location.search, location.hash]);

  const cameFromValidCampaign = useMemo(() => {
    const validCampaigns = _get(
      siteCopy,
      'pageCopy.emailLoginReminder.campaigns',
      [],
    );
    return validCampaigns.includes(query.utm_campaign);
  }, [query, siteCopy]);

  useEffect(() => {
    if (!auth.isLoaded) return;
    if (
      auth.isEmpty &&
      allowSignIn &&
      cameFromValidCampaign &&
      !isAuthPage &&
      !isCTAModalEnabled
    ) {
      setOpen(true);
      setReminderWasShown(true);
    }
  }, [auth, allowSignIn, cameFromValidCampaign, isAuthPage, isCTAModalEnabled]);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handleLogin = useCallback(() => {
    const redirect = encodeURIComponent(
      `${location.pathname}${location.search}`,
    );
    const hasCustomAuthenticationEnabled = _get(
      clientDetails,
      'customAuthentication.enabled',
    );
    const pathname = hasCustomAuthenticationEnabled ? '/auth' : '/login';
    history.push({ pathname, search: `?redirect=${redirect}` });
    setOpen(false);
  }, [clientDetails, history, location.pathname, location.search]);

  return { open, handleClose, handleLogin, reminderWasShown };
}
